/**
 * CalendarHeatmap
 * Calendário visual mostrando intensidade de publicações por dia do mês
 *
 * Fase 2.1 do Plano de Evolução - Calendar Heatmap
 */

import { useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';
import { PainelCard, PainelStat, PainelStatRow } from './PainelCard';

export interface DayData {
  date: string;
  day: number;
  dayOfWeek: number;
  total: number;
  processed: number;
  pending: number;
  discarded: number;
  intensity: number; // 0-4
}

export interface MonthlyHeatmapData {
  year: number;
  month: number;
  monthName: string;
  days: DayData[];
  summary: {
    total: number;
    processed: number;
    pending: number;
    discarded: number;
    averagePerDay: number;
    peakDay: DayData;
    daysWithPublications: number;
  };
}

// Intensidade 0-4 em tons de primary sobre muted
const INTENSITY_CLASSES = [
  'bg-muted text-foreground hover:bg-accent',
  'bg-primary/20 text-foreground hover:bg-primary/30',
  'bg-primary/40 text-foreground hover:bg-primary/50',
  'bg-primary/70 text-primary-foreground hover:bg-primary/80',
  'bg-primary text-primary-foreground hover:bg-primary/90'
];

const intensityClass = (intensity: number) => INTENSITY_CLASSES[intensity] ?? INTENSITY_CLASSES[0];

const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function MonthNavButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon-sm" onClick={onClick} aria-label={label}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

interface CalendarHeatmapProps {
  data: MonthlyHeatmapData;
  onDayClick?: (day: DayData) => void;
  onMonthChange?: (year: number, month: number) => void;
  selectedDate?: string;
  className?: string;
}

export function CalendarHeatmap({
  data,
  onDayClick,
  onMonthChange,
  selectedDate,
  className = ''
}: CalendarHeatmapProps) {
  const [hoveredDay, setHoveredDay] = useState<DayData | null>(null);

  // Calcular offset do primeiro dia (quantos dias vazios antes do dia 1)
  const firstDayOfWeek = data.days[0]?.dayOfWeek || 0;

  // Handlers de navegação
  const handlePreviousMonth = () => {
    const newMonth = data.month === 1 ? 12 : data.month - 1;
    const newYear = data.month === 1 ? data.year - 1 : data.year;
    onMonthChange?.(newYear, newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = data.month === 12 ? 1 : data.month + 1;
    const newYear = data.month === 12 ? data.year + 1 : data.year;
    onMonthChange?.(newYear, newMonth);
  };

  const handleCurrentMonth = () => {
    const today = new Date();
    onMonthChange?.(today.getFullYear(), today.getMonth() + 1);
  };

  const isToday = (day: DayData): boolean => {
    const today = new Date();
    return day.day === today.getDate() &&
           data.month === today.getMonth() + 1 &&
           data.year === today.getFullYear();
  };

  const isSelected = (day: DayData): boolean => {
    return selectedDate === day.date;
  };

  return (
    <PainelCard
      title={<span className="capitalize">{data.monthName} de {data.year}</span>}
      icon={Calendar}
      className={className}
      action={
        <>
          <Button variant="ghost" size="sm" className="text-primary" onClick={handleCurrentMonth}>
            Mês Atual
          </Button>
          <MonthNavButton label="Mês anterior" onClick={handlePreviousMonth}>
            <ChevronLeft />
          </MonthNavButton>
          <MonthNavButton label="Próximo mês" onClick={handleNextMonth}>
            <ChevronRight />
          </MonthNavButton>
        </>
      }
    >
      {/* Grade do calendário */}
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-7 gap-1">
          {WEEK_DAYS.map(day => (
            <div key={day} className="py-1 text-center text-xs font-semibold text-muted-foreground">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {/* Células vazias antes do primeiro dia */}
          {Array.from({ length: firstDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}

          {data.days.map(day => {
            const today = isToday(day);
            const selected = isSelected(day);

            return (
              <button
                type="button"
                key={day.day}
                onClick={() => onDayClick?.(day)}
                onMouseEnter={() => setHoveredDay(day)}
                onMouseLeave={() => setHoveredDay(null)}
                aria-pressed={selected}
                className={cn(
                  'relative flex aspect-square min-w-0 flex-col items-center justify-center rounded-lg text-xs font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  intensityClass(day.intensity),
                  selected && 'ring-2 ring-foreground ring-offset-1 ring-offset-card',
                  today && !selected && 'ring-2 ring-ring'
                )}
                disabled={day.total === 0}
              >
                <span className="text-sm font-semibold">{day.day}</span>
                {day.total > 0 && (
                  <span className="text-[10px] opacity-80">{day.total}</span>
                )}
                {today && (
                  <span className="absolute -right-1 -top-1 size-2 rounded-full bg-foreground" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Legenda de intensidade */}
      <div className="flex items-center gap-2 border-t border-border pt-4 text-xs text-muted-foreground">
        <span>Menos</span>
        {INTENSITY_CLASSES.map((classes, intensity) => (
          <span key={intensity} className={cn('size-4 rounded-sm', classes.split(' ')[0])} />
        ))}
        <span>Mais</span>
      </div>

      {/* Detalhe do dia sob o cursor */}
      {hoveredDay && hoveredDay.total > 0 && (
        <div className="rounded-lg border border-border bg-muted/50 p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold text-foreground">
              {new Date(hoveredDay.date).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              })}
            </span>
            <span className="text-sm font-semibold text-primary">
              {hoveredDay.total} publicações
            </span>
          </div>
          <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
            <div>
              <span className="text-muted-foreground">Tratadas:</span>
              <span className="ml-1 font-semibold text-primary">{hoveredDay.processed}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Pendentes:</span>
              <span className="ml-1 font-semibold text-destructive">{hoveredDay.pending}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Descartadas:</span>
              <span className="ml-1 font-semibold text-muted-foreground">{hoveredDay.discarded}</span>
            </div>
          </div>
        </div>
      )}

      {/* Estatísticas do mês */}
      <PainelStatRow>
        <PainelStat label="Total no Mês" value={data.summary.total} tone="primary" />
        <PainelStat label="Média/Dia" value={data.summary.averagePerDay} />
        <PainelStat label="Dia Pico" value={`${data.summary.peakDay.day} (${data.summary.peakDay.total})`} />
        <PainelStat label="Dias Ativos" value={`${data.summary.daysWithPublications}/${data.days.length}`} />
      </PainelStatRow>
    </PainelCard>
  );
}
